import type { HTMLAttributes, ReactNode } from "react";
import { t } from "../i18n";

// The skeleton every page of the app is built from (byj-1wv.51.19): the page
// at its width, its head — the title, what stands beside it, the page's own
// actions at the line's end — the lead under the head, and what stands in the
// page's place while it loads or when it cannot be shown. What goes under the
// head is the page's own; this is only what every page has the same, so a new
// page looks like the rest by being built from it, not by copying one.

/**
 * How wide a page is: `wide` most pages, `full` a page that is a board (the
 * class dashboard).
 */
export type PageWidth = "wide" | "full";

/**
 * The page: as wide as it is given in the shell's column, up to its width.
 * `as` is "div" where the page is shown inside another (the class's student
 * page in its instructor's preview), which has the page's one `main`.
 */
export function Page({ width = "wide", as: Tag = "main", className, children, ...rest }: {
  width?: PageWidth;
  as?: "main" | "div";
  /** the page's own class beside page, for what is the page's alone */
  className?: string;
  children: ReactNode;
} & Omit<HTMLAttributes<HTMLElement>, "className" | "children">) {
  const names = ["page", width, className].filter(Boolean).join(" ");
  return (
    <Tag className={names} {...rest}>
      {children}
    </Tag>
  );
}

/**
 * A page's head: its title, then `meta` beside it — what the page is (a role,
 * a count, a quota) — and at the line's end `actions`, what the page does or
 * where it goes. The title takes the width its words need, the rest sits at
 * the line's end; when they do not fit, the title keeps its line and the rest
 * goes under it at its own size; on a phone two or more actions take that
 * line whole, each as wide as it can be. `long` is for a
 * title a person wrote (an exam's, a file's name), which may be long and
 * breaks anywhere.
 */
export function PageHead({ title, long = false, meta, actions }: {
  title: ReactNode;
  long?: boolean;
  meta?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <header className="page-head">
      <h1 className={long ? "long-title" : undefined}>{title}</h1>
      {meta && <span className="row wrap small head-meta">{meta}</span>}
      {actions && <span className="row wrap head-actions">{actions}</span>}
    </header>
  );
}

/** What the page is for, in a line or two under its head: secondary text. */
export function PageLead({ children }: { children: ReactNode }) {
  return <p className="muted small page-lead">{children}</p>;
}

/** The page while what it shows loads. */
export function PageLoading() {
  return <main className="narrow muted">{t("common:loading")}</main>;
}

/**
 * The page when it cannot be shown: the error that stopped it, or `message`,
 * why it is not for this person; then `children`, the way back or a retry.
 */
export function PageNotice({ error, message, children }: {
  error?: string;
  message?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <main className="narrow card">
      {error !== undefined ? <p className="error">{t("common:error", { m: error })}</p> : message !== undefined && <p>{message}</p>}
      {children}
    </main>
  );
}
